import { CONVERSATION_STATUS, VIEW_MODE } from 'helpers/enum';
import { ConversationActionTypes } from 'types/actions/conversation';
import { Conversation, CurrentConversation, SupportingContent } from 'types/reducers/conversation';

import { conversationConstant } from '../actionTypes';

interface ConversationState {
  conversations: {
    data: Conversation[];
    total_item: number;
    total_page: number;
    current_page: number;
  };
  status: CONVERSATION_STATUS;
  process_conversation_id: string;
  processingStreamingChunkStatus: boolean;
  current: CurrentConversation;
  selectedCitation: {
    page: number;
    total_page: number;
    file_name: string;
    file_url: string;
    fileType: string;
    supporting_contents: SupportingContent[];
  };
  viewMode: VIEW_MODE;
  isLimitReached: boolean;
}

const initialState: ConversationState = {
  conversations: {
    data: [],
    total_item: 0,
    total_page: 0,
    current_page: 0,
  },
  status: CONVERSATION_STATUS.IDLE,
  process_conversation_id: '',
  processingStreamingChunkStatus: false,
  current: {
    conversation_id: '',
    type: '',
    data: [],
    answerChunks: {
      order: -1,
      chunk: '',
    },
    setTimeoutFinalAnswer: false,
  },
  selectedCitation: {
    page: 1,
    total_page: 1,
    file_name: '',
    file_url: '',
    fileType: '',
    supporting_contents: [],
  },
  viewMode: VIEW_MODE.CHAT,
  isLimitReached: false,
};

const conversationReducer = (
  state: ConversationState = initialState,
  action: ConversationActionTypes
): ConversationState => {
  switch (action.type) {
    case conversationConstant.UPDATE_CONVERSATIONS: {
      return {
        ...state,
        conversations: action.payload,
      };
    }
    case conversationConstant.UPDATE_STATUS: {
      return {
        ...state,
        status: action.payload,
      };
    }
    case conversationConstant.UPDATE_CURRENT_QUESTION: {
      const tempData = [...state.current.data];
      const latestMessage = tempData[tempData.length - 1];

      latestMessage.answer = '';
      latestMessage.citations = [];

      tempData[tempData.length - 1] = {
        ...latestMessage,
        ...action.payload,
      };
      return {
        ...state,
        current: {
          ...state.current,
          data: tempData,
        },
      };
    }
    case conversationConstant.UPDATE_CURRENT_CONVERSATION: {
      return {
        ...state,
        current: {
          ...state.current,
          conversation_id: action.payload.conversation_id,
          type: action.payload.type,
        },
      };
    }
    case conversationConstant.UPDATE_PROCESS_CONVERSATION_ID: {
      return {
        ...state,
        process_conversation_id: action.payload,
      };
    }
    case conversationConstant.UPDATE_CURRENT_CONVERSATION_DATA: {
      return {
        ...state,
        current: {
          ...state.current,
          data: action.payload,
        },
      };
    }
    case conversationConstant.UPDATE_NEW_QUESTION: {
      return {
        ...state,
        current: {
          ...state.current,
          data: [...state.current.data, action.payload],
        },
      };
    }

    case conversationConstant.ADD_NEW_CONVERSATION: {
      return {
        ...state,
        conversations: {
          ...state.conversations,
          data: [action.payload, ...state.conversations.data],
          total_item: state.conversations.total_item + 1,
        },
      };
    }
    case conversationConstant.UPDATE_SELECTED_CITATION: {
      return {
        ...state,
        selectedCitation: {
          ...state.selectedCitation,
          ...action.payload,
        },
      };
    }
    case conversationConstant.UPDATE_NEW_ANSWER: {
      if (action.payload?.conversation_id === state.current.conversation_id) {
        const newChatData = [...state.current.data];
        const newMesssage = {
          ...action.payload,
          updated_at: action.payload.timestamp,
        };
        const lastMessage = newChatData[newChatData?.length - 1];
        newChatData[newChatData.length - 1] = {
          ...lastMessage,
          ...newMesssage,
        };
        return {
          ...state,
          current: {
            ...state.current,
            data: newChatData,
          },
          status: CONVERSATION_STATUS.IDLE,
        };
      }
      return { ...state, status: CONVERSATION_STATUS.IDLE };
    }

    case conversationConstant.UPDATE_PROCESSING_STREAMING_CHUNK_STATUS: {
      return {
        ...state,
        processingStreamingChunkStatus: action.payload,
      };
    }

    case conversationConstant.UPDATE_NEW_STREAMING_ANSWER_CHUNKS: {
      if (
        action.payload?.conversation_id === state.current.conversation_id &&
        action.payload.order > state.current.answerChunks.order
      ) {
        return {
          ...state,
          current: {
            ...state.current,
            answerChunks: {
              order: action.payload.order,
              chunk: action.payload.part_of_answer,
            },
          },
        };
      }
      return state;
    }

    case conversationConstant.CLEAR_STREAMING_ANSWER_CHUNKS: {
      return {
        ...state,
        current: {
          ...state.current,
          answerChunks: {
            order: -1,
            chunk: '',
          },
        },
      };
    }

    case conversationConstant.SET_TIMEOUT_FOR_FINAL_ANSWER: {
      return {
        ...state,
        current: {
          ...state.current,
          setTimeoutFinalAnswer: action.payload,
        },
      };
    }

    case conversationConstant.UPDATE_VIEW_MODE: {
      return {
        ...state,
        viewMode: action.payload,
      };
    }
    case conversationConstant.UPDATE_LIMIT_STATUS: {
      return {
        ...state,
        isLimitReached: action.payload,
      };
    }
    case conversationConstant.RESET_DATA: {
      return initialState;
    }
    default:
      return state;
  }
};

export default conversationReducer;
